import { normalizeCallerId } from '@/common/application/utils/stringUtils';
import { formatDate, convertToWindowsTimezone } from '@/common/application/utils/timeUtils';
import { normalizeNullableNumber } from '@/common/application/utils/numberUtils';
import { CreateRaptorJobRequest } from '@/raptorjob/domain/job-create-type';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import timezoneMapping from '@/jobs/presentation/hooks/timezoneMapping.json';

const toHHmm = (time: Date | string | null | undefined) => {
  if (!time) return undefined;
  if (typeof time === 'string') return time;
  return new Date(time).toTimeString().slice(0, 8);
};

/**
 * Formats a Date object to ISO string preserving the time components as selected by the user
 * Formats the date preserving local time components (what user selected) without UTC conversion
 * The API will use the timeZone field to interpret this datetime correctly
 * @param date Date object to format
 * @returns ISO string with local time components (e.g., "2025-12-12T10:25:00.000")
 */
const formatLocalDateTime = (date: Date): string => {
  // Extract local time components and format as ISO string without UTC conversion
  // This preserves "10:25 AM" as "10:25 AM" instead of converting to UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export const useMapERPJobPayload = () => {
  const mapERPPayload = ({ values }: { values: ERPJobFormValues }): CreateRaptorJobRequest => {
    // Use timeZone from form if provided, otherwise fallback to browser timezone
    const rawTimeZone = values.timeZone || (Intl?.DateTimeFormat?.().resolvedOptions().timeZone as string | undefined) || 'UTC';

    // Convert IANA identifier to Windows timezone name if needed
    // The backend expects a Windows timezone name and will convert it to IANA using TimeZoneService.GetIANATimezone()
    const timeZone = convertToWindowsTimezone(rawTimeZone, timezoneMapping as Record<string, string>);
    if (!values.startDate) {
      throw new Error('Start date is required');
    }

    const startDate = formatDate(values.startDate, 'yyyy-MM-dd')!;

    const jobTypeId = Number(values.jobTypeId);
    const customerId = Number(values.customerId);
    const jobSiteId = Number(values.jobSiteId);

    if (![jobTypeId, customerId, jobSiteId].every(Number.isFinite)) {
      throw new Error('Invalid job type, customer, or job site');
    }

    // Format endDate if it's an overnight job (endDate is provided AND overnightJob is true)
    const isOvernight = values.overnightJob ?? false;
    const endDate = values.endDate && isOvernight ? formatDate(values.endDate, 'yyyy-MM-dd') : null;

    const dispatchTimes = {
      startTime: toHHmm(values.startTime),
      endTime: toHHmm(values.endTime),
      showUpTime: toHHmm(values.showUpTime),
      leaveTime: toHHmm(values.leaveByTime),
      onJobTime: toHHmm(values.onJobTime)
    };

    return {
      jobTypeId,
      customerId,
      endDate,
      jobSiteId,
      ...(values.name && values.name.length > 0 && { name: values.name }),
      startDate,
      description: values.jobDescription || '',
      internalNotes: values.jobNotes || '',
      crewNotes: values.notesForCrew || '',
      timeZone,
      dispatchTimes,
      legacy: values.legacy,
      ...(values.dispatcherId !== undefined && { dispatcherId: values.dispatcherId ?? null }),
      ...(values.dispatchLocationId !== undefined && { dispatchLocationId: values.dispatchLocationId ?? null }),
      ...(values.estimatedVolume !== null && values.estimatedVolume !== undefined && { estimatedYards: values.estimatedVolume }),
      ...(values.estimatedArea !== null && values.estimatedArea !== undefined && { estimatedArea: values.estimatedArea }),
      ...(values.estimatedDuration !== null && values.estimatedDuration !== undefined && { estimatedDuration: values.estimatedDuration }),
      ...(values.estimatedClearedTime !== null &&
        values.estimatedClearedTime !== undefined && { estimatedClearedTime: toHHmm(values.estimatedClearedTime) }),
      ...(values.callerId !== undefined && { callerId: normalizeCallerId(values.callerId) }),
      ...(values.calledAt !== undefined && {
        calledAt:
          values.calledAt === null
            ? null
            : typeof values.calledAt === 'string'
              ? values.calledAt
              : formatLocalDateTime(values.calledAt as Date)
      }),
      ...(values.confirmedBy !== undefined && { confirmedBy: values.confirmedBy ?? null }),
      ...(values.confirmedAt !== undefined && {
        confirmedAt:
          values.confirmedAt === null
            ? null
            : typeof values.confirmedAt === 'string'
              ? values.confirmedAt
              : formatLocalDateTime(values.confirmedAt as Date)
      }),
      ...(values.cod !== undefined && { cod: values.cod ?? null }),
      ...(values.poNumber !== undefined && { poNumber: values.poNumber || null }),
      ...(values.salesAgentId !== undefined && { salesAgentId: values.salesAgentId ?? null }),
      ...(values.salesRegionId !== undefined && { salesRegionId: values.salesRegionId ?? null }),
      ...(values.vendorId !== undefined && { vendorId: normalizeNullableNumber(values.vendorId) }),
      ...(values.flag1Id !== undefined && { flag1Id: values.flag1Id ?? null }),
      ...(values.flag2Id !== undefined && { flag2Id: values.flag2Id ?? null }),
      ...(values.flag3Id !== undefined && { flag3Id: values.flag3Id ?? null }),
      ...(values.sequence !== undefined && { sequence: values.sequence ?? null }),
      sizeId: values.equipmentSizeId ?? null,
      ...(values.equipmentMaxSizeId && { maxRequestedId: values.equipmentMaxSizeId }),
      ...(values.strictSize && { strictSize: values.strictSize }),
      ...(values.equipmentClassId && { classId: values.equipmentClassId }),
      ...(values.equipmentTypeId && { typeId: values.equipmentTypeId }),
      resources: [
        {
          equipmentIds: values.equipmentUnitId != null && typeof values.equipmentUnitId === 'number' ? [values.equipmentUnitId] : [],
          crew:
            values.employees && values.employees.length > 0
              ? values.employees
                  .filter((employee) => employee.roleId != null && employee.employeeId != null)
                  .map((employee) => ({
                    roleId: employee.roleId!,
                    employeeId: employee.employeeId!
                  }))
              : [],
          items: (values.extraSystems || []).map((extraSystem) => extraSystem.extraSystemId).filter((id): id is number => id != null)
        }
      ]
    };
  };

  return { mapERPPayload };
};
